import { useState, useEffect, useContext } from "react";
import { connect } from "react-redux";
import moment from "moment-timezone";
import Grid from "../../../components/Grid";
import { ConvertToMoney } from "../../../utils/formattingUtils";
import { OrderPaymentStatusValueMap } from "../../../utils/enumsUtil";
import { PermissionContext } from "../../../hooks/PermissionContext";
import ActionButtons from "./actionButtons";

const BankingOrdersTable = ({
    activeTab,
    statuses = [],
    search,
    onSearchChange,
    reduxGetAllBankingOrders,
    orders,
}) => {
    const { hasPermission } = useContext(PermissionContext);
    const [currentPage, setCurrentPage] = useState(1)
    const [offset, setOffset] = useState(10);

    const loadOrders = (page, currentOffset, currentSearch) => {
        reduxGetAllBankingOrders({
            page: page,
            offset: currentOffset,
            search: currentSearch,
            status: statuses,
        });
    };

    useEffect(() => {
        if(!activeTab) return;
        
        loadOrders(currentPage, offset, search);
    }, [activeTab, currentPage, offset, search]);

    const columns = [
        {
            title: '#',
            render: (rowData) => {
                return <span>{rowData.idPayment}</span>;
            },
            visible: false,
        },
        {
            title: "ID Order",
            render: (rowData) => {
                return <span>{rowData.idOrder}</span>;
            },
            field: 'TMOP.id_order',
            searchable: true,
        },
        {
            title: 'ID Externo',
            render: (rowData) => {
                return <span>{rowData.order.externalId}</span>;
            },
            field: 'TMO.externalId',
            searchable: true,
        },
        {
            title: 'ID Address',
            render: (rowData) => {
                return <span>{rowData.order.idAddress}</span>;
            },
            field: 'TMO.idAddress',
            searchable: true,
        },
        {
            title: 'Estado',
            render: (rowData) => {
                return <span>{OrderPaymentStatusValueMap[rowData.status]}</span>;
            },
        },
        {
            title: "País",
            render: (rowData) => {
                return <span>{rowData.order.preOrder[0].city?.department?.country?.description ?? rowData.order.preOrder[0].countryNameReceiver}</span>;
            },
        },
        {
            title: 'Ciudad',
            render: (rowData) => {
                return <span>{rowData.order.preOrder[0].city?.description || rowData.order.preOrder[0].cityNameReceiver}</span>;
            },
            field: 'TMCY.description',
            searchable: true,
        },
        {
            title: 'Departamento',
            render: (rowData) => {
                return (
                    <span>
                        {rowData.order.preOrder[0].city?.department?.description || rowData.order.preOrder[0].stateNameReceiver}
                    </span>
                );
            },
            field: 'TMCD.description',
            searchable: true,
        },
        {
            title: 'Dirección Destinatario',
            render: (rowData) => {
                return <span>{rowData.order.preOrder[0].address}</span>;
            },
            field: 'TMPO.address',
            searchable: true,
        },
        {
            title: 'Destinatario',
            render: (rowData) => {
                return <span>{rowData.order.preOrder[0]?.firstName} {rowData.order.preOrder[0]?.lastName}</span>;
            },
            field: 'firstName-lastName',
            visible: hasPermission('feature:banking-show-recipient'),
        },
        {
            title: 'Teléfono Destinatario',
            render: (rowData) => {
                return <span>{rowData.order.preOrder[0]?.reference1}</span>;
            },
            field: 'reference1',
            visible: hasPermission('feature:banking-show-reference1'),
        },
        {
            title: "Proveedor de Pago",
            render: (rowData) => {
                return <span>{rowData.paymentProvider?.name ?? ''}</span>;
            },
        },
        {
            title: "Tipo de Pago",
            render: (rowData) => {
                return <span>{rowData.paymentType?.name ?? ''}</span>;
            },
        },
        {
            title: 'Método Pago',
            render: (rowData) => {
                return <span>{rowData.paymentMethod?.name ?? ''}</span>;
            },
        },
        {
            title: 'Valor a Recaudar',
            render: (rowData) => {
                return <span>{ConvertToMoney(rowData.amount, rowData.currency)}</span>;
            },
        },
        {
            title: 'Compañía',
            render: (rowData) => {
                return <span>{rowData.order.company.description}</span>;
            }
        },
        {
            title: 'Fecha',
            render: (rowData) => {
                return <span>{rowData.createdAt && moment(rowData.createdAt).tz('America/Bogota').format('DD-MM-YYYY HH:mm:ss') || ''}</span>;
            },
        },
        {
            title: 'Fecha de Pago',
            render: (rowData) => {
                return <span>{rowData.paidAt && moment(rowData.paidAt).tz('America/Bogota').format('DD-MM-YYYY HH:mm:ss') || ''}</span>;
            },
        },
        {
            title: 'Fecha Expiración',
            render: (rowData) => {
                return <span>{rowData.expiresAt && moment(rowData.expiresAt).tz('America/Bogota').format('DD-MM-YYYY HH:mm:ss') || ''}</span>;
            }
        },
        {
            title: 'Notificado',
            render: (rowData) => {
                const { notification } = rowData;
                if (!notification || notification.sent == null) {
                    return <span>-</span>;
                }
                return <span>{notification.sent ? 'Sí' : 'No'}</span>;
            }
        }
    ];

    if (activeTab === 'forManagement') {
        columns.push({
            title: 'Opciones',
            render: (rowData) => {
                return (
                    <>
                        <ActionButtons rowData={rowData} loadOrders={() => loadOrders(currentPage, offset, search)}/>
                    </>
                );
            }
        })
    }

    return <>
        <Grid
            cols={columns}
            data={orders && orders.items ? orders.items : []}
            page={orders && orders.page ? Number(orders.page) : currentPage}
            pages={orders && orders.totalPages ? Number(orders.totalPages) : 1}
            total={orders && orders.total ? orders.total : 0}
            offset={offset}
            onChangePage={(value) => {
                setCurrentPage(value);
            }}
            onChangeRange={(value) => {
                setCurrentPage(1);
                setOffset(value);
            }}
            defaultValue={search}
            onChangeSearch={(value) => {
                setCurrentPage(1);
                onSearchChange(value);
            }}
        />
    </>
}

const mapStateToProps = (state) => {
    return {
        orders: state.bankingOrdersState.orders,
    }
}

const mapDispatchToProps = (dispatch) => {
    return {
        reduxGetAllBankingOrders: (payload) =>
            dispatch({
                type: 'FETCH_BANKING_ORDERS_REQUEST',
                value: payload
            }),
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(BankingOrdersTable)